/**
 * Desktop share panel (Windows software has no OS share sheet in Electron).
 * Shows the image + full message (store links + CTA) with one-click targets.
 * Image cannot be attached through a URL, so targets copy it to the clipboard
 * first — the user pastes it (Ctrl+V) into the chat.
 */
import { openSupportLink } from "./support";

export type ShareDialogOptions = {
  title: string;
  text: string;
  url?: string;
  file?: File | null;
  downloadName?: string;
};

let root: HTMLElement | null = null;
let current: ShareDialogOptions | null = null;
let previewUrl = "";

function fullMessage(o: ShareDialogOptions): string {
  if (o.url && !o.text.includes(o.url)) return `${o.text}\n\n${o.url}`;
  return o.text;
}

function setStatus(msg: string): void {
  const el = root?.querySelector<HTMLElement>(".share-dialog-status");
  if (el) el.textContent = msg;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

async function copyImage(file: File): Promise<boolean> {
  try {
    if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) return false;
    await navigator.clipboard.write([new ClipboardItem({ [file.type || "image/png"]: file })]);
    return true;
  } catch {
    return false;
  }
}

function saveImage(file: File, name: string): void {
  const href = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

function targetUrl(target: string, o: ShareDialogOptions): string {
  const msg = fullMessage(o);
  const enc = encodeURIComponent;
  const link = o.url || "";
  switch (target) {
    case "whatsapp":
      return `https://wa.me/?text=${enc(msg)}`;
    case "telegram":
      return `https://t.me/share/url?url=${enc(link)}&text=${enc(msg)}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}&quote=${enc(msg)}`;
    case "x": {
      const short = o.text.split("\n").find((l) => l.trim())?.slice(0, 200) || o.title;
      return `https://twitter.com/intent/tweet?text=${enc(short)}&url=${enc(link)}`;
    }
    case "email":
      return `mailto:?subject=${enc(o.title)}&body=${enc(msg)}`;
    default:
      return "";
  }
}

async function onTarget(target: string): Promise<void> {
  const o = current;
  if (!o) return;
  if (target === "copy-text") {
    setStatus((await copyText(fullMessage(o))) ? "Message with links copied ✓" : "Copy failed — select the text and copy.");
    return;
  }
  if (target === "copy-image") {
    if (!o.file) return;
    setStatus((await copyImage(o.file)) ? "Image copied ✓ — paste with Ctrl+V" : "Image copy not supported — use Save image.");
    return;
  }
  if (target === "save") {
    if (o.file) saveImage(o.file, o.downloadName ?? o.file.name);
    setStatus("Image saved to Downloads ✓");
    return;
  }
  if (target === "more") {
    try {
      const data: ShareData = { title: o.title, text: fullMessage(o) };
      if (o.file && navigator.canShare?.({ files: [o.file] })) data.files = [o.file];
      await navigator.share(data);
      close();
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setStatus("System share unavailable.");
    }
    return;
  }
  const imageCopied = o.file ? await copyImage(o.file) : false;
  if (target === "email" && !imageCopied) {
    await copyText(fullMessage(o));
  }
  openSupportLink(targetUrl(target, o));
  setStatus(
    imageCopied
      ? "Message filled in ✓ · Image copied — press Ctrl+V in the chat to attach it."
      : "Message filled in ✓ · Use Save image to attach the picture.",
  );
}

function close(): void {
  if (!root) return;
  root.hidden = true;
  if (previewUrl) URL.revokeObjectURL(previewUrl);
  previewUrl = "";
  current = null;
}

function ensureRoot(): HTMLElement {
  if (root) return root;
  root = document.createElement("div");
  root.className = "share-dialog";
  root.hidden = true;
  root.innerHTML = `
    <div class="share-dialog-card" role="dialog" aria-modal="true" aria-labelledby="share-dialog-title">
      <div class="share-dialog-head">
        <h3 id="share-dialog-title">Share</h3>
        <button type="button" class="share-dialog-close" data-share="close" aria-label="Close">✕</button>
      </div>
      <div class="share-dialog-body">
        <img class="share-dialog-preview" alt="Share image preview" />
        <div class="share-dialog-side">
          <label class="share-dialog-label">Message (links + code included)
            <textarea class="share-dialog-text" readonly rows="9"></textarea>
          </label>
          <div class="share-dialog-targets">
            <button type="button" class="share-target is-whatsapp" data-share="whatsapp">WhatsApp</button>
            <button type="button" class="share-target is-telegram" data-share="telegram">Telegram</button>
            <button type="button" class="share-target is-facebook" data-share="facebook">Facebook</button>
            <button type="button" class="share-target is-x" data-share="x">X</button>
            <button type="button" class="share-target" data-share="email">Email</button>
          </div>
          <div class="share-dialog-tools">
            <button type="button" class="auth-chip" data-share="copy-text">Copy message</button>
            <button type="button" class="auth-chip" data-share="copy-image">Copy image</button>
            <button type="button" class="auth-chip" data-share="save">Save image</button>
            <button type="button" class="auth-chip" data-share="more" hidden>More apps…</button>
          </div>
          <p class="share-dialog-status" aria-live="polite"></p>
        </div>
      </div>
    </div>`;
  root.addEventListener("click", (e) => {
    if (e.target === root) {
      close();
      return;
    }
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-share]");
    if (!btn) return;
    if (btn.dataset.share === "close") close();
    else void onTarget(btn.dataset.share!);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && root && !root.hidden) close();
  });
  document.body.appendChild(root);
  return root;
}

export function openShareDialog(opts: ShareDialogOptions): void {
  const el = ensureRoot();
  current = opts;
  el.querySelector("#share-dialog-title")!.textContent = opts.title;
  el.querySelector<HTMLTextAreaElement>(".share-dialog-text")!.value = fullMessage(opts);
  const img = el.querySelector<HTMLImageElement>(".share-dialog-preview")!;
  if (previewUrl) URL.revokeObjectURL(previewUrl);
  previewUrl = opts.file ? URL.createObjectURL(opts.file) : "";
  img.hidden = !opts.file;
  if (opts.file) img.src = previewUrl;
  el.querySelectorAll<HTMLElement>('[data-share="copy-image"], [data-share="save"]').forEach((b) => {
    b.hidden = !opts.file;
  });
  el.querySelector<HTMLElement>('[data-share="more"]')!.hidden = typeof navigator.share !== "function";
  setStatus("");
  el.hidden = false;
}
